<template>
  <v-card>
    <v-card-title class="text-headline-small bg-grey-lighten-2" primary-title>
      {{ 'Modifier un matériau' }}
    </v-card-title>
    <v-card-text>
      <v-container fluid>
        <div v-if="hasWarning" class="text-error">
          {{ payload.warning }}
        </div>
        <v-form v-model="valid" @submit.prevent="submit" ref="form">
          <v-row>
            <v-col cols="12">
              <v-text-field
                label="Nom"
                :disabled="!isUserAdmin"
                :rules="[rules.required]"
                v-model="material.name"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-select
                :disabled="!isUserAdmin"
                :items="units"
                v-model="material.unit"
                label="Unité de mesure"
                :item-title="item => item.name + (item.description !== '' ? ' - ' + item.description : '')"
                item-value="id"
                :rules="[rules.required]"
              ></v-select>
            </v-col>
            <v-col cols="12" v-if="isUserAdmin">
              <v-text-field
                label="Prix de l'unité ($ CA)"
                v-model="material.unitPrice"
                :rules="[rules.digit]"
                type="number"
              ></v-text-field>
            </v-col>
            <v-col cols="12" v-if="!isUserAdmin">
              <v-row class="fill-height align-center">
                <v-col cols="6">
                  <v-text-field
                    label="Prix de l'unité ($ CA)"
                    v-model="material.userPrice"
                    :rules="[rules.digit]"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col class="text-right" cols="6">
                  <v-btn variant="text" class="pr-0" @click="resetUserPrice">
                    Réinitialiser
                    <v-icon end class="pl-4" color="secondary" size="24">mdi-refresh</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-text-field
                :disabled="!isUserAdmin"
                label="Temps de la main d'oeuvre (heures)"
                v-model="material.laborConstant"
                :rules="[rules.digit]"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-select
                :disabled="!isUserAdmin"
                @update:model-value="changeHourlyRates()"
                :items="labors"
                v-model="material.labor"
                label="Corps de métier"
                item-title="name"
                item-value="id"
                :rules="[rules.required]"
              ></v-select>
            </v-col>
            <v-col cols="12">
              {{ material.laborHourlyRate }}
            </v-col>
          </v-row>
        </v-form>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="primary" variant="text" @click="close"> Annuler </v-btn>
      <v-btn color="primary" variant="text" @click="submit" :disabled="!valid"> Confirmer </v-btn>
    </v-card-actions>
    <v-snackbar v-model="showSnackBar" :timeout="6000" color="error" y="bottom">
      {{ snackBarText }}
      <v-btn variant="text" @click="showSnackBar = false">Fermer</v-btn>
    </v-snackbar>
  </v-card>
</template>

<script>
import { DialogPropsMixin } from '@/components/dialog/mixins';
import { Save, Get, DeleteUserMaterial } from '@/_services/materials';
import { GetAll } from '@/_services/units';
import { GetLabors } from '@/_services/labors';

export default {
  name: 'ModalCatalogMaterial',
  mixins: [DialogPropsMixin],
  computed: {
    hasWarning() {
      return !!this.payload.warning;
    },
    isUserAdmin() {
      return this.$store.getters['user/isAdmin'];
    },
  },
  data() {
    return {
      snackBarText: '',
      showSnackBar: false,
      valid: true,
      rules: {
        required: value => !!value || 'Champ requis.',
        digit: value => {
          const pattern = /^\d*(\.\d+)?$/;
          if (pattern.test(value) && Number.isFinite(Number.parseFloat(value))) {
            return true;
          } else if (value === null || value === '') {
            return true;
          }
          return 'Coût invalide.';
        },
      },
      units: [],
      labors: [],
      material: {
        id: 0,
        name: '',
        unit: null,
        labor: null,
        laborHourlyRate: '',
        unitPrice: 0,
        laborConstant: 0,
        userPrice: 0,
      },
      errors: {
        name: false,
        unit: false,
        laborHourlyRate: false,
        unitPrice: false,
        laborConstant: false,
        labor: false,
        userPrice: false,
      },
    };
  },
  mounted() {
    this.getUnits();
    this.getLabors();
    this.getDataFromApi();
  },
  methods: {
    resetUserPrice() {
      DeleteUserMaterial(this.payload.materialId).then(() => {
        this.$emit('close');
      });
    },
    close() {
      this.resetData();
      this.$emit('close');
    },
    getUnits() {
      GetAll().then(rep => {
        this.units = rep;
      });
    },
    getLabors() {
      GetLabors().then(rep => {
        this.labors = rep;
      });
    },
    getDataFromApi() {
      Get(this.payload.materialId).then(rep => {
        this.material.id = rep.id;
        this.material.name = rep.name;
        this.material.unit = rep.unit.id;
        this.material.labor = rep.labor.id;
        this.material.laborHourlyRate = this.formatLaborRate(rep.labor);
        this.material.unitPrice = rep.unitPrice;
        this.material.userPrice = rep.userPrice === null ? rep.unitPrice : rep.userPrice;
        this.material.laborConstant = rep.laborConstant;
      });
    },
    changeHourlyRates() {
      const labor = this.labors.find(labor => labor.id === this.material.labor);
      this.material.laborHourlyRate = this.formatLaborRate(labor);
    },
    formatLaborRateFrom(id) {
      const labor = this.labors.find(x => x.id === id);
      if (labor) {
        return this.formatLaborRate(labor);
      }
      return '';
    },
    formatLaborRate(labor) {
      return `${this.formatTextRate(labor.residentialCost)} - ${this.formatTextRate(
        labor.commercialCost,
      )} - ${this.formatTextRate(labor.industrialCost)} - ${this.formatTextRate(labor.civilEngineeringCost)} `;
    },
    formatTextRate(rate) {
      const text = rate || 'n/a';
      return rate ? text + '$' : text;
    },
    submit() {
      if (this.valid) {
        this.material.unitPrice = Number(this.material.unitPrice);
        if (this.material.userPrice) {
          this.material.userPrice = Number(this.material.userPrice);
        }

        Save(this.material, this.isUserAdmin)
          .then(() => {
            if (this.payload.emits) {
              this.$bus.emit(this.payload.emits);
            }
            this.close();
          })
          .catch(error => {
            this.showSnackBar = true;
            this.snackBarText = error;
          });
      }
    },
    resetData() {
      this.resetErrors();
      this.resetMaterial();
      this.$refs.form.reset();
    },
    resetErrors() {
      this.errors = {
        name: false,
        unit: false,
        laborHourlyRate: false,
        unitPrice: false,
        laborConstant: false,
      };
    },
    resetMaterial() {
      this.material = {
        id: 0,
        name: '',
        unit: null,
        labor: null,
        laborHourlyRate: '',
        unitPrice: 0,
        laborConstant: 0,
      };
    },
  },
  watch: {
    mode(newVal) {
      if (newVal === 'edit') {
        this.getDataFromApi();
      }
    },
  },
};
</script>
